/**
 * The single FHIR namespace authority for all Eluve-minted systems — the ONE
 * definition (ENG-5115); `@eluve/nestjs-medplum`, `@eluve/plan-shapes`, and
 * `@eluve/fhir-client` all re-export from here.
 *
 * A FHIR `Identifier.system` (and a `meta.tag` `Coding.system`) names the
 * *assigning authority* — "Eluve mints this value" — which is a stable logical
 * fact in every environment. It is therefore a CONSTANT, never derived from a
 * deployment/request URL: environment isolation comes from separate Medplum
 * servers + per-tenant Project scoping, not from the namespace string. Deriving
 * it from the host would silently break conditional upsert any time data moves
 * between environments (DR restore, prod→staging clone, tenant migration,
 * domain rename).
 *
 * Grammar conventions on top of this base (match `@eluve/plan-shapes`):
 * - extension URLs:        `<ns>/StructureDefinition/<name>`
 * - coding systems:        `<ns>/CodeSystem/<name>`
 * - identifier systems:    `<ns>/<entity>-id` (pathless, pre-dates the grammar;
 *   byte-compatibility with stored identifiers forbids changing it)
 * - marker/tag systems:    `<ns>/<name>` (pathless, same reason)
 */
export const ELUVE_FHIR_NAMESPACE = 'https://eluve.com';

/**
 * Entities whose Eluve app ID is stamped as a FHIR `Identifier`.
 *
 * This is the extension point for new app-ID-keyed resource types: to add one
 * (e.g. Condition, Goal, Observation), add its key here and call
 * `buildIdentifierSystem(<key>)` — no bespoke identifier-system strings anywhere.
 */
export type FhirIdentifierEntity =
  | 'tenant'
  | 'patient'
  | 'encounter'
  | 'location'
  // The end-of-encounter draft CarePlan keys off the appointment id
  // (`https://eluve.com/care-plan-id|<appointmentId>`) so the matcher's first
  // draft wins idempotently (ENG-4699). Kebab-cased to match the existing
  // `tenant-id`/`encounter-id` systems (the key is interpolated verbatim).
  | 'care-plan'
  // The FHIR Task tracking the async care-plan-generation job, also keyed off
  // the appointment id (`https://eluve.com/care-plan-generation-job-id|<appointmentId>`)
  // so the in-flight signal exists before the draft does and dedupes against the
  // graphile jobKey (ENG-4706).
  | 'care-plan-generation-job'
  // A Clinical Task (ENG-5218): an AI-extracted follow-up action stored as a
  // clinician-facing FHIR Task, keyed by its own uuid
  // (`https://eluve.com/clinical-task-id|<uuid>`). Deliberately a DIFFERENT
  // system from `care-plan-generation-job` so the two Task populations stay
  // disjoint in every query.
  | 'clinical-task'
  // The FHIR Task tracking the async clinical-task-extraction job, keyed off
  // the appointment id — same in-flight-signal pattern as
  // `care-plan-generation-job`, and a different system from `clinical-task` so
  // the marker never shows up in task lists.
  | 'clinical-task-extraction-job'
  // The FHIR Task tracking clinical-findings extraction after note generation.
  // The Care Plan UI reads this appointment-keyed marker to cover the pipeline
  // before the downstream care-plan-generation Task exists.
  | 'clinical-findings-extraction-job'
  // A tenant member's mirrored Practitioner, keyed by the eluve user id
  // (`https://eluve.com/user-id|<userId>`). The value is the USER id — the
  // same person in two tenants is two Practitioners (one per project) carrying
  // the same identifier, disambiguated by project scoping.
  | 'user'
  // A member's per-location PractitionerRole, keyed by the compound
  // `<userId>:<locationId>` (see {@link userLocationIdentifierValue}) so the
  // (user, location) pair upserts idempotently.
  | 'user-location';

/**
 * Mint the `Identifier.system` for an app-ID-keyed resource:
 * `<ELUVE_FHIR_NAMESPACE>/<entity>-id` (e.g. `https://eluve.com/patient-id`).
 *
 * This is the only place runtime identifier systems are constructed. It lives
 * in this shared lib rather than the backend one so the frontend can mint them
 * too — the three constants below used to be hand-copied strings carrying a
 * comment that said "the frontend can't reach `@eluve/nestjs-medplum`".
 */
export const buildIdentifierSystem = (entity: FhirIdentifierEntity): string =>
  `${ELUVE_FHIR_NAMESPACE}/${entity}-id`;

/** The mirror's durable key for a Location: `<ns>/location-id|<eluve id>`. */
export const FHIR_LOCATION_ID_SYSTEM = buildIdentifierSystem('location');

/**
 * The practitioner mirror's durable key: `<ns>/user-id|<eluve user id>`.
 * The value is the USER id — the same person in two tenants is two
 * Practitioners (one per Medplum project) carrying the same identifier.
 */
export const FHIR_USER_ID_SYSTEM = buildIdentifierSystem('user');

/**
 * Identifier system for a member's PER-LOCATION PractitionerRole:
 * `<ns>/user-location-id|<userId>:<locationId>` (compound value via
 * {@link userLocationIdentifierValue}).
 */
export const FHIR_USER_LOCATION_ID_SYSTEM =
  buildIdentifierSystem('user-location');

/**
 * `meta.tag` system for the eluve→FHIR mirror's own markers.
 *
 * {@link FHIR_MIRROR_DEACTIVATED_CODE} is a tag rather than `active: false`
 * because Patient already spends that value on "left the practice" (the profile
 * rail and the status endpoint both read it). A patient whose row was merged
 * away must not render as having left the practice, and clearing left-practice
 * must not resurrect a deleted row.
 */
export const FHIR_MIRROR_TAG_SYSTEM = `${ELUVE_FHIR_NAMESPACE}/mirror`;

/** The mirrored eluve row is gone; this resource is kept only for references. */
export const FHIR_MIRROR_DEACTIVATED_CODE = 'source-row-deleted';

/**
 * This resource version was written BY the mirror, projecting an eluve row.
 *
 * Provenance only. The tag rides `meta.tag`, which any writer that reads a
 * resource and PUTs it back carries forward, so a tagged version does NOT prove
 * the mirror wrote *this* one — never treat it as authority for skipping a
 * correctness check.
 */
export const FHIR_MIRROR_AUTHORED_CODE = 'mirror-write';

/** The compound identifier value for a (user, location) PractitionerRole. */
export function userLocationIdentifierValue(
  userId: string,
  locationId: string,
): string {
  return `${userId}:${locationId}`;
}
